import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { AppContext } from '../context/AppContext'
import DragDrop from '../reusable/DragDrop'
import fetchData from '../utils/fetchData'
import getCookie from '../utils/getCookie'
import styles from '../../styles/scss/module/Post.module.scss'

const AddStoreProduct = () => {
  const { setAlert } = useContext(AppContext)
  const [isSubmitting, setSubmitting] = useState(false)
  const [inputData, setInputData] = useState({
    title: '',
    description: '',
    keyFeatures: '',
    price: '',
    discount: '0',
    stock: '10',
    warrantyPeriod: '',
    isPreOrder: true,
    advancePercent: '10',
    status: true,
  })
  const [featuredImage, setFeaturedImage] = useState()
  const [extraImages, setExtraImages] = useState([])
  const [metaData, setMetaData] = useState({
    title: '',
    description: '',
    keywords: '',
    author: '',
    properties: [],
  })
  const navigate = useNavigate()

  const handleChange = ({ target }) => {
    const { name, value, type, checked, files } = target
    if (type === 'file') {
      if (name === 'extraImages') {
        const images = []
        for (let i = 0; i < files.length; i++)
          images.push({
            name: files?.[i]?.name,
            size: files?.[i]?.size,
            preview: URL.createObjectURL(files?.[i]),
            file: files?.[i],
          })
        setExtraImages([...extraImages, ...images])
      } else
        setFeaturedImage({
          name: files?.[0]?.name,
          size: files?.[0]?.size,
          preview: URL.createObjectURL(files?.[0]),
          file: files?.[0],
        })
      return
    }
    setInputData({
      ...inputData,
      [name]: type === 'checkbox' ? checked : value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const formData = new FormData()
      const payload = {
        ...inputData,
        metaData: JSON.stringify({
          ...metaData,
          title: metaData.title || inputData.title,
          description: metaData.description || inputData.description,
        }),
      }
      for (let field in payload) formData.append(field, payload[field])
      featuredImage?.file &&
        formData.append('featuredImage', featuredImage?.file)
      for (let i = 0; i < extraImages?.length; i++)
        formData.append('extraImages', extraImages[i]?.file)

      await fetchData.post('/storeProduct', formData, {
        headers: { Authorization: `Bearer ${getCookie('eyevaryToken')}` },
      })
      setAlert({
        show: true,
        variant: 'success',
        message: 'Store product successfully added',
        icon: 'mdi mdi-check',
        duration: 5,
      })
      navigate('/store-products')
    } catch (error) {
      console.log({ ...error }, error)
      setAlert({
        show: true,
        variant: 'danger',
        message:
          error?.response?.data?.message ||
          'Something went wrong. Please try again.',
        icon: 'mdi mdi-close',
        duration: 5,
      })
    }
    setSubmitting(false)
  }

  return (
    <section className='content-wrapper'>
      <Form className={styles.post_wrapper} onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            <div className={styles.left}>
              <div className={styles.featured_image}>
                <h6>Featured Image</h6>
                <DragDrop
                  accept='image/*'
                  name='featuredImage'
                  files={featuredImage}
                  setFiles={setFeaturedImage}
                  onChange={handleChange}
                />
                <label htmlFor='extraImages' className='d-block pnt'>
                  <i className='mdi mdi-image-size-select-actual'></i> Add Extra
                  Images
                </label>
                <input
                  accept='image/*'
                  type='file'
                  id='extraImages'
                  name='extraImages'
                  className='d-none'
                  multiple
                  onChange={handleChange}
                />
                <div className={styles.images_preview}>
                  <Row>
                    {extraImages?.map((image, i) => (
                      <Col md={4} sm={6} key={i}>
                        <div className={styles.preview_item}>
                          <span
                            onClick={() =>
                              setExtraImages(
                                extraImages?.filter((_, j) => j !== i)
                              )
                            }>
                            <i className='mdi mdi-close'></i>
                          </span>
                          <img src={image?.preview} alt={image?.name} />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
              <div className={styles.title}>
                <h6>Title</h6>
                <Form.Control
                  type='text'
                  size='lg'
                  placeholder='Product title'
                  name='title'
                  value={inputData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles.description}>
                <h6>Description</h6>
                <Form.Control
                  as='textarea'
                  rows='6'
                  size='lg'
                  placeholder='Product description'
                  name='description'
                  value={inputData.description}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles.key_features}>
                <h6>Key Features (One feature per line)</h6>
                <Form.Control
                  as='textarea'
                  rows='5'
                  placeholder={'Antenna: 2*5dBi external antennas\nData Rate: 300Mbps, Frequency: 2.4GHz\nPort: 1x 10/100Mbps WAN, 3x 10/100Mbps LAN\nCoverage Area: Up to 800 sq. ft.'}
                  name='keyFeatures'
                  value={inputData.keyFeatures}
                  onChange={handleChange}
                />
                <Form.Text className='text-muted'>
                  Enter each feature on a separate line. They will be displayed as bullet points.
                </Form.Text>
              </div>
            </div>
          </Col>
          <Col lg={4}>
            <div className={styles.right}>
              <div className='mb-3'>
                <h6>Price (BDT)</h6>
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  name='price'
                  value={inputData.price}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className='mb-3'>
                <h6>Discount (%)</h6>
                <Form.Control
                  type='number'
                  min='0'
                  max='100'
                  name='discount'
                  value={inputData.discount}
                  onChange={handleChange}
                />
              </div>
              <div className='mb-3'>
                <h6>Stock Quantity</h6>
                <Form.Control
                  type='number'
                  min='0'
                  placeholder='Available stock (0 for out of stock)'
                  name='stock'
                  value={inputData.stock}
                  onChange={handleChange}
                  required
                />
                <Form.Text className='text-muted'>
                  When stock reaches 0, pre-orders will be accepted if enabled below.
                </Form.Text>
              </div>
              <div className='mb-3'>
                <h6>Warranty period</h6>
                <Form.Control
                  type='text'
                  placeholder='e.g. 1 Year'
                  name='warrantyPeriod'
                  value={inputData.warrantyPeriod}
                  onChange={handleChange}
                />
              </div>
              <div className='mb-3 form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='isPreOrder'
                  name='isPreOrder'
                  checked={inputData.isPreOrder}
                  onChange={handleChange}
                />
                <label className='form-check-label' htmlFor='isPreOrder'>
                  Take Pre-order (when stock is out)
                </label>
              </div>
              {inputData.isPreOrder && (
                <div className='mb-3'>
                  <h6>Advance percent (%)</h6>
                  <Form.Control
                    type='number'
                    min='1'
                    max='100'
                    name='advancePercent'
                    value={inputData.advancePercent}
                    onChange={handleChange}
                    required
                  />
                  <Form.Text className='text-muted'>
                    Default 10% advance required when customer places pre-order.
                  </Form.Text>
                </div>
              )}
              <div className='mb-3 form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='status'
                  name='status'
                  checked={inputData.status}
                  onChange={handleChange}
                />
                <label className='form-check-label' htmlFor='status'>
                  Published
                </label>
              </div>
              <Button type='submit' disabled={isSubmitting} className='w-100'>
                Save product{' '}
                {isSubmitting && (
                  <Spinner animation='border' size='sm' className='ms-2' />
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </section>
  )
}

export default AddStoreProduct
